import React from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  MessagesSquare,
  Inbox,
  BookOpen,
  Radio,
  Clock,
  ArrowRight,
  type LucideIcon,
} from 'lucide-react'
import { analyticsAPI } from '../services/api'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/lib/utils'

const Dashboard: React.FC = () => {
  const { data: dashboardData, isLoading: dashboardLoading } = useQuery({
    queryKey: ['dashboard'],
    queryFn: analyticsAPI.getDashboard,
  })

  const { data: activityData, isLoading: activityLoading } = useQuery({
    queryKey: ['activity'],
    queryFn: () => analyticsAPI.getActivity(10),
  })

  const { data: performanceData } = useQuery({
    queryKey: ['performance'],
    queryFn: () => analyticsAPI.getPerformance(7),
  })

  const d = dashboardData?.data

  const stats: {
    name: string
    value: number
    detail: string
    icon: LucideIcon
    href: string
    /** Only set when the number means someone has to do something. */
    needsAttention?: boolean
  }[] = [
    {
      name: 'Conversations',
      value: d?.conversations.total ?? 0,
      detail: `${d?.conversations.today ?? 0} today`,
      icon: MessagesSquare,
      href: '/conversations',
    },
    {
      name: 'Open inquiries',
      value: d?.inquiries.pending ?? 0,
      detail: `${d?.inquiries.today ?? 0} today`,
      icon: Inbox,
      href: '/inquiries',
      needsAttention: (d?.inquiries.pending ?? 0) > 0,
    },
    {
      name: 'Knowledge items',
      value: d?.knowledge.active ?? 0,
      detail: `${d?.knowledge.total ?? 0} total`,
      icon: BookOpen,
      href: '/knowledge',
    },
    {
      name: 'Active now',
      value: d?.conversations.active ?? 0,
      detail: 'Open sessions',
      icon: Radio,
      href: '/conversations',
    },
  ]

  const activityIcons: Record<string, LucideIcon> = {
    conversation: MessagesSquare,
    inquiry: Inbox,
    knowledge: BookOpen,
  }

  const quickActions = [
    { label: 'Review open inquiries', href: '/inquiries' },
    { label: 'Manage knowledge base', href: '/knowledge' },
    { label: 'Update branding', href: '/branding' },
  ]

  const activities = activityData?.data.activities ?? []

  return (
    <div className="space-y-6">
      {/* Counters. Colour is reserved for the one figure that implies work to
          do — colouring all four would make none of them mean anything. */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => (
          <Link
            key={stat.name}
            to={stat.href}
            className="rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <Card className="h-full transition-colors hover:border-primary/40">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <span className="text-sm font-medium text-muted-foreground">{stat.name}</span>
                  <stat.icon
                    className={cn(
                      'h-4 w-4',
                      stat.needsAttention ? 'text-warning' : 'text-muted-foreground/60'
                    )}
                  />
                </div>

                {dashboardLoading ? (
                  <Skeleton className="mt-3 h-8 w-16" />
                ) : (
                  <p className="mt-3 text-3xl font-semibold tabular-nums tracking-tight">
                    {stat.value.toLocaleString()}
                  </p>
                )}

                <p className="mt-1 text-xs text-muted-foreground">{stat.detail}</p>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* System health — only real, server-reported figures. */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">System</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            {performanceData?.data ? (
              <>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Uptime</span>
                  <Badge variant="success">{performanceData.data.systemHealth.uptime}</Badge>
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Messages per conversation</span>
                  <span className="font-medium tabular-nums">
                    {performanceData.data.systemHealth.avgMessagesPerConversation}
                  </span>
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Total conversations</span>
                  <span className="font-medium tabular-nums">
                    {performanceData.data.systemHealth.totalConversations}
                  </span>
                </div>
              </>
            ) : (
              <div className="space-y-3">
                <Skeleton className="h-5 w-full" />
                <Skeleton className="h-5 w-full" />
                <Skeleton className="h-5 w-full" />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Shortcuts. Previously these were buttons with no handler — they now
            navigate, which is what they always appeared to promise. */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Jump to</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-2 sm:grid-cols-3">
            {quickActions.map((action) => (
              <Link
                key={action.href}
                to={action.href}
                className="group flex items-center justify-between gap-2 rounded-md border px-3 py-2.5 text-sm font-medium transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                {action.label}
                <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
              </Link>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Activity feed */}
      <Card>
        <CardHeader className="flex-row items-center justify-between space-y-0 pb-3">
          <CardTitle className="text-sm">Recent activity</CardTitle>
          <Link
            to="/conversations"
            className="text-sm font-medium text-primary underline-offset-4 hover:underline"
          >
            View all
          </Link>
        </CardHeader>

        <CardContent>
          {activityLoading ? (
            <div className="space-y-4">
              {[0, 1, 2].map((i) => (
                <div key={i} className="flex gap-3">
                  <Skeleton className="h-8 w-8 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-1/3" />
                    <Skeleton className="h-3 w-2/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : activities.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-10 text-center">
              <Clock className="h-8 w-8 text-muted-foreground/40" />
              <p className="text-sm font-medium">No activity yet</p>
              <p className="max-w-sm text-sm text-muted-foreground">
                Conversations, inquiries and knowledge updates appear here as they happen.
              </p>
            </div>
          ) : (
            <ul className="space-y-1">
              {activities.map((activity: any, index: number) => {
                const Icon = activityIcons[activity.type] ?? Clock
                return (
                  <li key={activity.id}>
                    <div className="flex gap-3 py-2.5">
                      <div className="relative flex flex-col items-center">
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
                          <Icon className="h-4 w-4" />
                        </span>
                        {index !== activities.length - 1 && (
                          <span className="mt-1 w-px flex-1 bg-border" aria-hidden="true" />
                        )}
                      </div>

                      <div className="flex min-w-0 flex-1 justify-between gap-4 pt-1">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium">{activity.title}</p>
                          <p className="truncate text-sm text-muted-foreground">
                            {activity.description}
                          </p>
                        </div>
                        <time
                          dateTime={activity.timestamp}
                          className="shrink-0 text-xs tabular-nums text-muted-foreground"
                        >
                          {new Date(activity.timestamp).toLocaleDateString([], {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </time>
                      </div>
                    </div>
                  </li>
                )
              })}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export default Dashboard
